import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Play, Pause, Volume2, VolumeX, X, Camera, Scan, Sparkles, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export default function ARViewer() {
  const { cardId } = useParams();
  const [searchParams] = useSearchParams();
  const [arData, setArData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cardDetected, setCardDetected] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  
  const videoRef = useRef(null);
  const cameraRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimeoutRef = useRef(null);

  useEffect(() => {
    fetchARData();
    return () => {
      // Cleanup camera on unmount
      stopCamera();
    };
  }, [cardId]);

  const fetchARData = async () => {
    try {
      if (cardId && cardId !== 'card' && cardId !== 'test') {
        try {
          const response = await axios.get(`${API}/ar/greeting/${cardId}`);
          setArData(response.data);
          setLoading(false);
          return;
        } catch (err) {
          console.error('Error fetching AR data:', err);
        }
      }
      
      // Fallback to URL params
      setArData({
        message: searchParams.get('message') || 'You have a special greeting!',
        animation: searchParams.get('animation') || 'hearts',
        reveal_message: searchParams.get('reveal') || '🎉 Surprise!',
        video_url: searchParams.get('video') || null,
      });
      setLoading(false);
    } catch (error) {
      console.error('Error in fetchARData:', error);
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      const constraints = {
        video: {
          facingMode: 'environment', // Use back camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (cameraRef.current) {
        cameraRef.current.srcObject = stream;
        await cameraRef.current.play();
      }
      
      setCameraActive(true);
      setScanning(true);
      
      // Simulate card detection after a scanning period
      // In a real implementation, this would use image recognition
      scanTimeoutRef.current = setTimeout(() => {
        setCardDetected(true);
        setScanning(false);
      }, 3000);
      
    } catch (error) {
      console.error('Camera error:', error);
      setCameraError(error.message || 'Could not access camera. Please allow camera permissions.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    setCameraActive(false);
    setScanning(false);
    setCardDetected(false);
  };

  const playVideo = useCallback(() => {
    if (videoRef.current && arData?.video_url) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(e => {
        console.log('Autoplay blocked, tap video to play');
      });
    }
  }, [arData]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play().then(() => setIsPlaying(true));
      }
    }
  };

  const handleManualDetect = () => {
    // Allow manual trigger for testing/accessibility
    setCardDetected(true);
    setScanning(false);
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
  };

  const resetAR = () => {
    setCardDetected(false);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setScanning(true);
    scanTimeoutRef.current = setTimeout(() => {
      setCardDetected(true);
      setScanning(false);
    }, 3000);
  };

  const closeAR = () => {
    stopCamera();
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  // Auto-play video when card is detected
  useEffect(() => {
    if (cardDetected && arData?.video_url) {
      playVideo();
    }
  }, [cardDetected, arData, playVideo]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-purple-900 via-pink-800 to-orange-700 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xl">Loading AR Experience...</p>
        </div>
      </div>
    );
  }

  // AR Camera View
  if (cameraActive) {
    return (
      <div className="fixed inset-0 bg-black">
        {/* Camera Feed */}
        <video
          ref={cameraRef}
          className="absolute inset-0 w-full h-full object-cover"
          playsInline
          muted
        />
        
        {/* Scanning Overlay */}
        {scanning && !cardDetected && (
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Scan Frame */}
            <div className="relative w-72 h-48 md:w-96 md:h-64">
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-cyan-400 rounded-tl-lg"></div>
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-cyan-400 rounded-tr-lg"></div>
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-cyan-400 rounded-bl-lg"></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-cyan-400 rounded-br-lg"></div>
              
              {/* Scanning line animation */}
              <div className="absolute inset-x-4 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-scan"></div>
              
              {/* Scan icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Scan className="w-16 h-16 text-cyan-400 animate-pulse" />
              </div>
            </div>
            
            {/* Instructions */}
            <div className="absolute bottom-32 left-0 right-0 text-center">
              <p className="text-white text-lg font-medium bg-black/50 mx-8 py-3 px-6 rounded-xl backdrop-blur-sm">
                📷 Point camera at your greeting card
              </p>
            </div>
            
            {/* Manual trigger button */}
            <button
              onClick={handleManualDetect}
              className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-sm"
            >
              Tap when card is in view
            </button>
          </div>
        )}
        
        {/* Video Overlay when card detected */}
        {cardDetected && arData?.video_url && (
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Video container with AR effects */}
            <div className="relative w-[85%] max-w-md aspect-video">
              {/* Glow effect */}
              <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-2xl blur-lg opacity-60 animate-pulse"></div>
              
              {/* Video */}
              <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl">
                <video
                  ref={videoRef}
                  src={arData.video_url.startsWith('http') ? arData.video_url : `${BACKEND_URL}${arData.video_url}`}
                  className="w-full h-full object-cover"
                  playsInline
                  loop
                  muted={!soundEnabled}
                  onClick={togglePlayPause}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
                
                {/* Play button overlay */}
                {!isPlaying && (
                  <div 
                    className="absolute inset-0 flex items-center justify-center bg-black/30"
                    onClick={togglePlayPause}
                  >
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                      <Play className="w-8 h-8 text-purple-600 ml-1" />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Floating decorations */}
              <div className="absolute -top-4 -left-4 text-3xl animate-bounce">✨</div>
              <div className="absolute -top-4 -right-4 text-3xl animate-bounce" style={{animationDelay: '0.2s'}}>💖</div>
              <div className="absolute -bottom-4 -left-4 text-3xl animate-bounce" style={{animationDelay: '0.4s'}}>🎉</div>
              <div className="absolute -bottom-4 -right-4 text-3xl animate-bounce" style={{animationDelay: '0.6s'}}>⭐</div>
            </div>
            
            {/* Message banner */}
            {arData?.message && (
              <div className="absolute bottom-24 left-4 right-4 text-center">
                <div className="inline-block bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-xl">
                  <p className="text-purple-900 font-bold text-lg">{arData.message}</p>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* AR Controls */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
          {cardDetected && (
            <>
              <button 
                onClick={togglePlayPause}
                className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <button 
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all"
              >
                {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
              <button 
                onClick={resetAR}
                className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
        
        {/* Close button */}
        <button 
          onClick={closeAR}
          className="absolute top-4 right-4 w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-all"
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* Camera error message */}
        {cameraError && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-6">
            <div className="bg-white rounded-2xl p-6 max-w-sm text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <Camera className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Camera Access Required</h3>
              <p className="text-gray-600 mb-4">{cameraError}</p>
              <Button onClick={() => { setCameraError(null); startCamera(); }}>
                Try Again
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Start Screen
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-900 via-pink-800 to-orange-700 flex items-center justify-center p-4">
      <div className="max-w-sm w-full text-center space-y-6">
        {/* AR Icon */}
        <div className="relative">
          <div className="w-28 h-28 mx-auto bg-white/10 backdrop-blur-xl rounded-3xl flex items-center justify-center shadow-2xl border border-white/20">
            <Camera className="w-14 h-14 text-white" />
          </div>
          <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center animate-bounce">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
        </div>
        
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 space-y-4 border border-white/20">
          <h1 className="text-3xl font-bold text-white">AR Video Greeting</h1>
          <p className="text-white/80">Experience your special video message in augmented reality!</p>
          
          {/* Instructions */}
          <div className="space-y-3 text-left">
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">1</div>
              <span>Tap "Start AR Camera" below</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">2</div>
              <span>Point your camera at the greeting card</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">3</div>
              <span>Watch the magic video appear! ✨</span>
            </div>
          </div>
          
          {/* Message preview */}
          {arData?.message && (
            <div className="bg-white/20 rounded-xl p-4 mt-4">
              <p className="text-white text-lg">"{arData.message}"</p>
            </div>
          )}
        </div>

        {/* Start AR Button */}
        <Button 
          onClick={startCamera}
          className="w-full py-6 text-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all hover:scale-105"
        >
          <Camera className="w-7 h-7 mr-3" />
          Start AR Camera
        </Button>
        
        {/* Fallback option */}
        {arData?.video_url && (
          <button 
            onClick={() => {
              setCameraActive(true);
              setCardDetected(true);
              setScanning(false);
            }}
            className="text-white/60 text-sm underline hover:text-white/90 transition-colors"
          >
            Or just play the video without AR
          </button>
        )}
      </div>
      
      {/* Custom CSS for scanning animation */}
      <style>{`
        @keyframes scan {
          0% { top: 10%; }
          50% { top: 90%; }
          100% { top: 10%; }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
